/**
 * Payment Methods page — UI mirrors the Flutter app's payment_screen so
 * web and app users see identical features:
 *
 *   1. Saved Cards section — list with brand icons, last4, expiry,
 *      default badge, "Set default" + "Remove" actions.
 *   2. Add New Card form — collapsible, with brand auto-detection,
 *      MM/YY formatter, CVC, cardholder name.
 *   3. Payment Type tabs — Card / Wallet (ACH) / Installment — used as
 *      a preview/selector for checkout flows. UI-only on this page.
 *   4. Wallet (ACH) form — routing #, account #, account type.
 *   5. Installment options — 3/6/12 months ($100 minimum).
 *
 * No backend changes — uses existing endpoints (`/payment-methods`,
 * `/payment-methods/setup`, `/payment-methods/attach`,
 * `/payment-methods/:id/default`, DELETE `/payment-methods/:id`).
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  CreditCard,
  Plus,
  Star,
  Trash2,
  CheckCircle2,
  Wallet,
  Calendar,
  Building2,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { cn } from "../../lib/utils";
import {
  getPaymentMethods,
  attachPaymentMethod,
  setDefaultPaymentMethod,
  deletePaymentMethod,
  PaymentMethod,
} from "../../services/payment-methods.api";

// ─── Helpers ────────────────────────────────────────────────────────────────

function detectCardBrand(cardNumber: string): string {
  const num = cardNumber.replace(/\D/g, "");
  if (/^4/.test(num)) return "visa";
  if (/^5[1-5]/.test(num) || /^2[2-7]/.test(num)) return "mastercard";
  if (/^3[47]/.test(num)) return "amex";
  if (/^6(?:011|5)/.test(num)) return "discover";
  return "card";
}

function getBrandColor(brand: string): string {
  switch (brand.toLowerCase()) {
    case "visa":
      return "bg-blue-600";
    case "mastercard":
      return "bg-orange-500";
    case "amex":
      return "bg-blue-500";
    case "discover":
      return "bg-orange-600";
    default:
      return "bg-zinc-700";
  }
}

function getBrandLabel(brand: string): string {
  const map: Record<string, string> = {
    visa: "VISA",
    mastercard: "Mastercard",
    amex: "Amex",
    discover: "Discover",
  };
  return map[brand.toLowerCase()] ?? brand.toUpperCase();
}

function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

function formatCardNumber(value: string): string {
  return value
    .replace(/\D/g, "")
    .slice(0, 19)
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

// ─── Sub-components ─────────────────────────────────────────────────────────

type PaymentTab = "card" | "wallet" | "installment";

function PaymentTypeTabs({
  active,
  onChange,
}: {
  active: PaymentTab;
  onChange: (t: PaymentTab) => void;
}) {
  const { t } = useTranslation('payment');
  const tabs: { id: PaymentTab; label: string; icon: typeof CreditCard }[] = [
    { id: "card", label: t('methodsPage.tabs.card'), icon: CreditCard },
    { id: "wallet", label: t('methodsPage.tabs.wallet'), icon: Wallet },
    { id: "installment", label: t('methodsPage.tabs.installment'), icon: Calendar },
  ];
  return (
    <div className="flex gap-2 p-1 bg-secondary/40 rounded-xl">
      {tabs.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all",
            active === id
              ? "bg-white dark:bg-card shadow text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Icon className="h-4 w-4" />
          {label}
        </button>
      ))}
    </div>
  );
}

function SavedCardRow({
  method,
  busy,
  onSetDefault,
  onDelete,
}: {
  method: PaymentMethod;
  busy: boolean;
  onSetDefault: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation('payment');
  const expMM = String(method.expMonth).padStart(2, "0");
  const expYY = String(method.expYear).slice(-2);
  return (
    <div
      className={cn(
        "rounded-2xl border bg-card p-4 flex items-center gap-4 transition-all",
        method.isDefault && "border-blue-600/40 ring-1 ring-blue-600/20"
      )}
    >
      <div
        className={cn(
          "h-12 w-16 rounded-lg flex items-center justify-center text-white text-[10px] font-bold tracking-wider",
          getBrandColor(method.brand)
        )}
      >
        {getBrandLabel(method.brand)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-foreground">
            •••• •••• •••• {method.last4}
          </p>
          {method.isDefault && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-600/10 text-blue-700 border border-blue-600/20">
              <CheckCircle2 className="h-3 w-3" />
              {t('methodsPage.default')}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {method.billingName ? `${method.billingName} · ` : ""}{t('methodsPage.expiresPrefix')} {expMM}/{expYY}
        </p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {!method.isDefault && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={onSetDefault}
            className="text-xs"
            title={t('methodsPage.setDefaultTitle')}
          >
            <Star className="h-4 w-4" />
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={onDelete}
          className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
          title={t('methodsPage.removeCardTitle')}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function AddCardForm({
  onAdded,
  onCancel,
}: {
  onAdded: () => Promise<void> | void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('payment');
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");
  const [name, setName] = useState("");
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const brand = detectCardBrand(cardNumber);
  const last4 = cardNumber.replace(/\D/g, "").slice(-4);
  const [expMM, expYY] = expiry.split("/");

  const isValid =
    cardNumber.replace(/\D/g, "").length >= 13 &&
    /^\d{2}$/.test(expMM ?? "") &&
    /^\d{2}$/.test(expYY ?? "") &&
    cvc.replace(/\D/g, "").length >= 3 &&
    name.trim().length > 1;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isValid) return;
    setSubmitting(true);
    try {
      // Attach via backend with safe metadata fallback (no raw card number sent).
      // In production with Stripe.js, replace with real `pm_xxx` from confirmSetup().
      await attachPaymentMethod({
        stripePaymentMethodId: `pm_sim_${Date.now()}`,
        isDefault: setAsDefault,
        brand,
        last4,
        expMonth: Number(expMM),
        expYear: 2000 + Number(expYY),
        billingName: name.trim(),
      });
      await onAdded();
    } catch (err) {
      setError((err as Error).message ?? t('methodsPage.saveCardError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border bg-card p-5 space-y-4"
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "h-10 w-14 rounded-md flex items-center justify-center text-white text-[10px] font-bold tracking-wider",
            getBrandColor(brand)
          )}
        >
          {getBrandLabel(brand)}
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            {t('methodsPage.addCardTitle')}
          </h3>
          <p className="text-xs text-muted-foreground">
            {t('methodsPage.addCardSubtitle', { brand: getBrandLabel(brand) })}
          </p>
        </div>
      </div>

      <div>
        <Label htmlFor="cardNumber" className="text-xs text-zinc-700">
          {t('methodsPage.cardNumber')}
        </Label>
        <Input
          id="cardNumber"
          inputMode="numeric"
          autoComplete="cc-number"
          value={cardNumber}
          onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
          placeholder={t('methodsPage.cardNumberPlaceholder')}
          className="text-zinc-900 placeholder:text-zinc-400"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="expiry" className="text-xs text-zinc-700">
            {t('methodsPage.expiryLabel')}
          </Label>
          <Input
            id="expiry"
            inputMode="numeric"
            autoComplete="cc-exp"
            value={expiry}
            onChange={(e) => setExpiry(formatExpiry(e.target.value))}
            placeholder={t('methodsPage.expiryPlaceholder')}
            className="text-zinc-900 placeholder:text-zinc-400"
          />
        </div>
        <div>
          <Label htmlFor="cvc" className="text-xs text-zinc-700">
            {t('methodsPage.cvc')}
          </Label>
          <Input
            id="cvc"
            inputMode="numeric"
            autoComplete="cc-csc"
            value={cvc}
            onChange={(e) => setCvc(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder={t('methodsPage.cvcPlaceholder')}
            className="text-zinc-900 placeholder:text-zinc-400"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="name" className="text-xs text-zinc-700">
          {t('methodsPage.cardholderName')}
        </Label>
        <Input
          id="name"
          autoComplete="cc-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('methodsPage.cardholderPlaceholder')}
          className="text-zinc-900 placeholder:text-zinc-400"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
        <input
          type="checkbox"
          checked={setAsDefault}
          onChange={(e) => setSetAsDefault(e.target.checked)}
          className="h-4 w-4 rounded border-zinc-300 text-blue-600"
        />
        {t('methodsPage.setAsDefault')}
      </label>

      {error && (
        <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
          className="flex-1"
        >
          {t('methodsPage.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={!isValid || submitting}
          className="flex-1"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t('methodsPage.saving')}
            </>
          ) : (
            t('methodsPage.saveCard')
          )}
        </Button>
      </div>
    </form>
  );
}

function WalletAchForm() {
  const { t } = useTranslation('payment');
  const [routing, setRouting] = useState("");
  const [account, setAccount] = useState("");
  const [accountType, setAccountType] = useState<"checking" | "savings">(
    "checking"
  );
  return (
    <div className="rounded-2xl border bg-card p-5 space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
          <Building2 className="h-5 w-5 text-emerald-700" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">{t('methodsPage.achTitle')}</h3>
          <p className="text-xs text-muted-foreground">
            {t('methodsPage.achSubtitle')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-zinc-700">{t('methodsPage.routingNumber')}</Label>
          <Input
            inputMode="numeric"
            value={routing}
            onChange={(e) =>
              setRouting(e.target.value.replace(/\D/g, "").slice(0, 9))
            }
            placeholder={t('methodsPage.routingPlaceholder')}
            className="text-zinc-900 placeholder:text-zinc-400"
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-700">{t('methodsPage.accountNumber')}</Label>
          <Input
            inputMode="numeric"
            value={account}
            onChange={(e) =>
              setAccount(e.target.value.replace(/\D/g, "").slice(0, 17))
            }
            placeholder={t('methodsPage.accountPlaceholder')}
            className="text-zinc-900 placeholder:text-zinc-400"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs text-zinc-700 mb-2 block">{t('methodsPage.accountType')}</Label>
        <div className="flex gap-2">
          {(["checking", "savings"] as const).map((typeValue) => (
            <button
              key={typeValue}
              type="button"
              onClick={() => setAccountType(typeValue)}
              className={cn(
                "flex-1 py-2 rounded-lg border text-sm capitalize",
                accountType === typeValue
                  ? "bg-blue-50 border-blue-600 text-blue-700"
                  : "bg-card border-border text-muted-foreground hover:bg-secondary/40"
              )}
            >
              {t(`methodsPage.${typeValue}`)}
            </button>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground">
        {t('methodsPage.achPrivacy')}
      </p>
    </div>
  );
}

function InstallmentOptions() {
  const { t } = useTranslation('payment');
  const [months, setMonths] = useState<3 | 6 | 12>(3);
  const options: Array<{ months: 3 | 6 | 12; label: string; note: string }> = [
    { months: 3, label: t('methodsPage.months3'), note: t('methodsPage.months3Note') },
    { months: 6, label: t('methodsPage.months6'), note: t('methodsPage.months6Note') },
    { months: 12, label: t('methodsPage.months12'), note: t('methodsPage.months12Note') },
  ];
  return (
    <div className="rounded-2xl border bg-card p-5 space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
          <Calendar className="h-5 w-5 text-purple-700" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            {t('methodsPage.installmentsTitle')}
          </h3>
          <p className="text-xs text-muted-foreground">
            {t('methodsPage.installmentsSubtitle')}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {options.map((opt) => (
          <button
            key={opt.months}
            type="button"
            onClick={() => setMonths(opt.months)}
            className={cn(
              "rounded-xl border p-3 text-left transition-all",
              months === opt.months
                ? "bg-blue-50 border-blue-600 ring-1 ring-blue-600/20"
                : "bg-card border-border hover:bg-secondary/40"
            )}
          >
            <p className="text-sm font-semibold text-foreground">{opt.label}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{opt.note}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────

export function PaymentMethodsPage() {
  const { t } = useTranslation('payment');
  const navigate = useNavigate();
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [activeTab, setActiveTab] = useState<PaymentTab>("card");
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await getPaymentMethods();
      setMethods(list);
    } catch (err) {
      setError((err as Error).message ?? t('methodsPage.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const handleSetDefault = async (id: string) => {
    setBusyId(id);
    try {
      await setDefaultPaymentMethod(id);
      await refresh();
    } catch (err) {
      setError((err as Error).message ?? t('methodsPage.setDefaultError'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('methodsPage.confirmRemove'))) return;
    setBusyId(id);
    try {
      await deletePaymentMethod(id);
      await refresh();
    } catch (err) {
      setError((err as Error).message ?? t('methodsPage.removeError'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-12">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="h-9 w-9 p-0"
            aria-label={t('methodsPage.backAria')}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold text-foreground">
              {t('methodsPage.title')}
            </h1>
            <p className="text-xs text-muted-foreground">
              {t('methodsPage.subtitle')}
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-6 space-y-6">
        {/* Type tabs */}
        <PaymentTypeTabs active={activeTab} onChange={setActiveTab} />

        {/* Saved cards / type-specific content */}
        {activeTab === "card" && (
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">
                {t('methodsPage.savedCards')}
              </h2>
              {!showAdd && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowAdd(true)}
                  className="text-xs"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t('methodsPage.addCard')}
                </Button>
              )}
            </div>

            {loading ? (
              <div className="rounded-2xl border bg-card p-8 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : methods.length === 0 ? (
              <div className="rounded-2xl border bg-card p-8 text-center">
                <div className="h-12 w-12 rounded-full bg-secondary/60 mx-auto flex items-center justify-center mb-3">
                  <CreditCard className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-foreground">
                  {t('methodsPage.noCardsTitle')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('methodsPage.noCardsBody')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {methods.map((m) => (
                  <SavedCardRow
                    key={m.id}
                    method={m}
                    busy={busyId === m.id}
                    onSetDefault={() => handleSetDefault(m.id)}
                    onDelete={() => handleDelete(m.id)}
                  />
                ))}
              </div>
            )}

            {showAdd && (
              <AddCardForm
                onCancel={() => setShowAdd(false)}
                onAdded={async () => {
                  setShowAdd(false);
                  await refresh();
                }}
              />
            )}

            {error && (
              <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </section>
        )}

        {activeTab === "wallet" && <WalletAchForm />}

        {activeTab === "installment" && <InstallmentOptions />}

        <p className="text-[11px] text-muted-foreground text-center pt-2">
          Powered by Stripe · cards encrypted in transit and at rest
        </p>
      </main>
    </div>
  );
}

export default PaymentMethodsPage;
